/** Markdown links embedded in calendar descriptions, e.g. [Agenda](https://…). */
const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|drl\/[^)\s]+)\)/g;

/** HTML anchors in calendar invites — keep the href, not the broken visible text. */
const HTML_ANCHOR_PATTERN = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>[\s\S]*?<\/a>/gi;

const HTML_TAG_PATTERN = /<[^>]+>/g;

/** Bare http(s) and internal drl/ short links. */
const BARE_URL_PATTERN = /(?:https?:\/\/[^\s<>[\]"')]+|drl\/[^\s<>[\]"')]+)/g;

const TRAILING_PUNCT = /[.,;:!?)\]}'"]+$/;

export type AgendaDescriptionLink = {
  url: string;
  label: string;
};

/** Peel markdown glue and trailing punctuation calendar descriptions often include. */
export const cleanAgendaUrl = (raw: string): string => {
  let url = raw.trim();

  // Broken markdown: https://foo/](https://foo/ — keep only the leading URL.
  const mdGlue = url.indexOf('](');
  if (mdGlue !== -1) {
    url = url.slice(0, mdGlue);
  }

  if (url.startsWith('<') && url.endsWith('>')) {
    url = url.slice(1, -1);
  }

  url = url.replace(TRAILING_PUNCT, '');
  return url;
};

/** Normalize bare internal short links (drl/…) to an openable http URL. */
export const normalizeAgendaLinkUrl = (url: string): string => {
  const cleaned = cleanAgendaUrl(url);
  if (cleaned.startsWith('drl/')) {
    return `http://${cleaned}`;
  }
  return cleaned;
};

const isValidHttpUrl = (url: string): boolean => {
  try {
    const parsed = new URL(normalizeAgendaLinkUrl(url));
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const isUrlLikeLabel = (label: string): boolean => /^https?:\/\//i.test(label.trim());

const decodeBasicHtmlEntities = (text: string): string =>
  text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

/** Flatten HTML descriptions to plain text while preserving anchor href targets. */
export const normalizeAgendaDescriptionText = (text: string): string => {
  if (!text.includes('<')) {
    return text;
  }
  let normalized = text.replace(HTML_ANCHOR_PATTERN, (_, href: string) =>
    decodeBasicHtmlEntities(href.trim())
  );
  normalized = normalized.replace(HTML_TAG_PATTERN, ' ');
  normalized = decodeBasicHtmlEntities(normalized);
  return normalized.replace(/\s+/g, ' ').trim();
};

/** True when a location string is just a conferencing URL (not a room name). */
export const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Location text worth showing under a row (excludes raw URLs). */
export const formatAgendaLocation = (location?: string): string | undefined => {
  const trimmed = location?.trim();
  if (!trimmed || isHttpUrl(trimmed)) {
    return undefined;
  }
  return trimmed;
};

type RawExtractedLink = {
  url: string;
  label?: string;
};

/** Collect URLs from markdown links first, then any remaining bare URLs. */
const collectUrlsFromText = (text: string): RawExtractedLink[] => {
  const normalized = normalizeAgendaDescriptionText(text);
  const links: RawExtractedLink[] = [];

  for (const match of normalized.matchAll(MARKDOWN_LINK_PATTERN)) {
    const url = normalizeAgendaLinkUrl(match[2]);
    const markdownLabel = match[1].trim();
    if (!url || !isValidHttpUrl(url)) {
      continue;
    }
    links.push({
      url,
      label: isUrlLikeLabel(markdownLabel) ? undefined : markdownLabel,
    });
  }

  const withoutMarkdown = normalized.replace(MARKDOWN_LINK_PATTERN, ' ');
  for (const match of withoutMarkdown.matchAll(BARE_URL_PATTERN)) {
    const url = normalizeAgendaLinkUrl(match[0]);
    if (!url || !isValidHttpUrl(url)) {
      continue;
    }
    links.push({url});
  }

  return links;
};

export const isZoomMeetingLink = (url: string): boolean => {
  try {
    const host = new URL(url).host;
    return host === 'zoom.us' || host.endsWith('.zoom.us');
  } catch {
    return false;
  }
};

const toTitleCase = (text: string): string =>
  text
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}` : word))
    .join(' ');

const shortLinkNameFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(normalizeAgendaLinkUrl(url));
    const host = parsed.hostname.replace(/^www\./, '');
    if (host !== 'dbx.link' && host !== 'drl') {
      return null;
    }
    const segments = parsed.pathname.split('/').filter(Boolean);
    const slug = segments[segments.length - 1];
    if (!slug) {
      return null;
    }
    const name = slug.replace(/[-_]+/g, ' ').trim();
    return name ? toTitleCase(name) : null;
  } catch {
    return null;
  }
};

/** Human-readable label for a link chip: paper title from the path, else the host. */
export const getAgendaLinkLabel = (url: string): string => {
  const paperName = paperNameFromUrl(url);
  if (paperName) {
    return paperName;
  }
  const shortName = shortLinkNameFromUrl(url);
  if (shortName) {
    return shortName;
  }
  try {
    return new URL(normalizeAgendaLinkUrl(url)).host;
  } catch {
    return url;
  }
};

const paperNameFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname;
    const segment = pathname
      .split('/')
      .find((part) => part.endsWith('.paper') || part.endsWith('.papert'));
    if (!segment) {
      return null;
    }
    const base = segment.replace(/\.papert?$/, '');
    return base.replace(/-/g, ' ').replace(/\s+/g, ' ').trim() || null;
  } catch {
    return null;
  }
};

const urlsMatch = (a: string, b: string): boolean => cleanAgendaUrl(a) === cleanAgendaUrl(b);

/** Pull the first Zoom link out of free-text for the Join button. */
export const extractZoomLinkFromText = (text?: string): string | undefined => {
  if (!text) {
    return undefined;
  }
  for (const {url} of collectUrlsFromText(text)) {
    if (isZoomMeetingLink(url)) {
      return url;
    }
  }
  return undefined;
};

/** Join URL from the item, falling back to a Zoom link embedded in the description. */
export const resolveAgendaJoinUrl = (joinUrl?: string, description?: string): string | undefined =>
  joinUrl ?? extractZoomLinkFromText(description);

/** Non-Zoom links from a calendar description, deduped and with friendly labels. */
export const extractAgendaDescriptionLinks = (
  description?: string,
  options?: {excludeUrls?: string[]}
): AgendaDescriptionLink[] => {
  if (!description) {
    return [];
  }

  const exclude = options?.excludeUrls ?? [];
  const seen = new Set<string>();
  const links: AgendaDescriptionLink[] = [];

  for (const {url, label: markdownLabel} of collectUrlsFromText(description)) {
    if (!url || seen.has(url) || isZoomMeetingLink(url)) {
      continue;
    }
    if (exclude.some((excluded) => urlsMatch(excluded, url))) {
      continue;
    }
    seen.add(url);
    links.push({
      url,
      label: markdownLabel ?? getAgendaLinkLabel(url),
    });
  }

  return links;
};

/** Whether an agenda row has link chips worth expanding into. */
export const hasAgendaExpandableLinks = (
  description?: string,
  joinUrl?: string,
  calendarUrl?: string
): boolean =>
  extractAgendaDescriptionLinks(description, {
    excludeUrls: [joinUrl, calendarUrl].filter((url): url is string => Boolean(url)),
  }).length > 0;

/** Whether a row should offer expand/details (description links and/or join). */
export const hasAgendaExpandableContent = (item: {
  description?: string;
  joinUrl?: string;
  calendarUrl?: string;
}): boolean => hasAgendaExpandableLinks(item.description, item.joinUrl, item.calendarUrl);
